import {
  TRIAL_FUND_CLAIMED_SEEN_KEY,
  TRIAL_FUND_EXCHANGED_KEY,
  TRIAL_FUND_GUEST_SNOOZE_DATE_KEY,
  TRIAL_FUND_GUIDE_DONE_KEY_PREFIX,
  TRIAL_FUND_LOGGED_MINI_POS_KEY,
  TRIAL_FUND_LOGGED_SNOOZE_DATE_KEY,
  TRIAL_FUND_SHOW_FEED_CARD_KEY,
  TRIAL_FUND_SIM_PENDING_KEY,
  TRIAL_FUND_SHARES,
} from '@/constants/trial-fund';

function safeRemoveLocal(key) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function safeRemoveSession(key) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * 登出：清体验金用户态本地缓存（换号不串引导 / 已兑换态）。
 * 不含 guest/logged view session（由 store.resetOnLogout 另清）。
 */
export function clearTrialFundUserStorage() {
  safeRemoveLocal(TRIAL_FUND_CLAIMED_SEEN_KEY);
  safeRemoveLocal(TRIAL_FUND_EXCHANGED_KEY);
  safeRemoveLocal(TRIAL_FUND_SHOW_FEED_CARD_KEY);
  safeRemoveLocal(TRIAL_FUND_GUEST_SNOOZE_DATE_KEY);
  safeRemoveLocal(TRIAL_FUND_LOGGED_SNOOZE_DATE_KEY);
  safeRemoveSession(TRIAL_FUND_LOGGED_MINI_POS_KEY);
  safeRemoveSession(TRIAL_FUND_SIM_PENDING_KEY);
  // 引导完成标记按 userId，登出不清除（同设备同号不再强弹）；换号用带 uid 的 key
}

/**
 * @returns {{ side: 'yes' | 'no', settleAtMs: number, shares: number } | null}
 */
export function readTrialFundSimPending() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(TRIAL_FUND_SIM_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const side = parsed?.side === 'no' ? 'no' : parsed?.side === 'yes' ? 'yes' : null;
    const settleAtMs = Number(parsed?.settleAtMs);
    const shares = Number(parsed?.shares);
    if (!side || !Number.isFinite(settleAtMs) || settleAtMs <= 0) return null;
    return {
      side,
      settleAtMs,
      shares: Number.isFinite(shares) && shares > 0 ? shares : TRIAL_FUND_SHARES,
    };
  } catch {
    return null;
  }
}

/**
 * @param {{ side: 'yes' | 'no', settleAtMs: number, shares?: number } | null} payload
 */
export function writeTrialFundSimPending(payload) {
  if (typeof sessionStorage === 'undefined') return;
  if (!payload) {
    safeRemoveSession(TRIAL_FUND_SIM_PENDING_KEY);
    return;
  }
  const side = payload.side === 'no' ? 'no' : 'yes';
  const settleAtMs = Number(payload.settleAtMs);
  if (!Number.isFinite(settleAtMs) || settleAtMs <= 0) {
    safeRemoveSession(TRIAL_FUND_SIM_PENDING_KEY);
    return;
  }
  try {
    sessionStorage.setItem(
      TRIAL_FUND_SIM_PENDING_KEY,
      JSON.stringify({
        side,
        settleAtMs,
        shares: Number(payload.shares) > 0 ? Number(payload.shares) : TRIAL_FUND_SHARES,
      })
    );
  } catch {
    /* ignore */
  }
}

export function clearTrialFundSimPending() {
  safeRemoveSession(TRIAL_FUND_SIM_PENDING_KEY);
}

function formatLocalDateKey(ms = Date.now()) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSnoozedForToday(storageKey, nowMs = Date.now()) {
  if (typeof localStorage === 'undefined') return false;
  const saved = String(localStorage.getItem(storageKey) ?? '').trim();
  if (!saved) return false;
  const today = formatLocalDateKey(nowMs);
  if (saved === today) return true;
  localStorage.removeItem(storageKey);
  return false;
}

function snoozeForToday(storageKey, nowMs = Date.now()) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey, formatLocalDateKey(nowMs));
}

export function isTrialFundGuestSnoozedForToday(nowMs = Date.now()) {
  return isSnoozedForToday(TRIAL_FUND_GUEST_SNOOZE_DATE_KEY, nowMs);
}

export function snoozeTrialFundGuestForToday(nowMs = Date.now()) {
  snoozeForToday(TRIAL_FUND_GUEST_SNOOZE_DATE_KEY, nowMs);
}

export function isTrialFundLoggedSnoozedForToday(nowMs = Date.now()) {
  return isSnoozedForToday(TRIAL_FUND_LOGGED_SNOOZE_DATE_KEY, nowMs);
}

export function snoozeTrialFundLoggedForToday(nowMs = Date.now()) {
  snoozeForToday(TRIAL_FUND_LOGGED_SNOOZE_DATE_KEY, nowMs);
}

export function readShowTrialFundFeedCard() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(TRIAL_FUND_SHOW_FEED_CARD_KEY) === '1';
}

export function setShowTrialFundFeedCard(show) {
  if (typeof localStorage === 'undefined') return;
  if (show) {
    localStorage.setItem(TRIAL_FUND_SHOW_FEED_CARD_KEY, '1');
    return;
  }
  localStorage.removeItem(TRIAL_FUND_SHOW_FEED_CARD_KEY);
}

export function readTrialFundExchanged() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(TRIAL_FUND_EXCHANGED_KEY) === '1';
}

export function setTrialFundExchanged(done) {
  if (typeof localStorage === 'undefined') return;
  if (done) {
    localStorage.setItem(TRIAL_FUND_EXCHANGED_KEY, '1');
    return;
  }
  localStorage.removeItem(TRIAL_FUND_EXCHANGED_KEY);
}

export function readTrialFundClaimedSeen() {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(TRIAL_FUND_CLAIMED_SEEN_KEY) === '1';
}

export function setTrialFundClaimedSeen(seen) {
  if (typeof localStorage === 'undefined') return;
  if (seen) {
    localStorage.setItem(TRIAL_FUND_CLAIMED_SEEN_KEY, '1');
    return;
  }
  localStorage.removeItem(TRIAL_FUND_CLAIMED_SEEN_KEY);
}

function guideDoneStorageKey(userId) {
  const uid = String(userId ?? '').trim();
  return uid
    ? `${TRIAL_FUND_GUIDE_DONE_KEY_PREFIX}${uid}`
    : `${TRIAL_FUND_GUIDE_DONE_KEY_PREFIX}anon`;
}

/** @param {string | number | null | undefined} userId */
export function readTrialFundGuideDone(userId) {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(guideDoneStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

/** @param {string | number | null | undefined} userId */
export function setTrialFundGuideDone(userId, done = true) {
  if (typeof localStorage === 'undefined') return;
  try {
    const key = guideDoneStorageKey(userId);
    if (done) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
